import React from 'react';
import { useTheme, Grid, Tooltip, TextField, Button, Stack, Box, Checkbox, Typography, FormControlLabel, Modal, CircularProgress } from '@mui/material';
import { Controller, SubmitHandler, useForm, FormProvider } from 'react-hook-form';
import { EditMode } from '../../../../data/enums';
import ManifestBuilder from '../manifest-builder';
import { useAmaWorkflowContext } from '../../../../context/ama-workflow-provider';

interface IFormInputs {
  ClientId: string;
  DisplayName: string;
  Description: string;
  IsActive: boolean;
  IsStrictMode: boolean;
  IsAutoStart: boolean;
  IsCensusHistoricAvailable: boolean;
  CensusCurrentYearSnapshot: string;
  SilverGlueJobName: string;
  GoldGlueJobName: string;
  NotificationRecipients: string;
  ManifestItems: Array<IManifestItem>;
}

interface IClientAddUpdateProps {
  mode: EditMode;
  setEditMode: React.Dispatch<React.SetStateAction<EditMode>>;
};

export default function ClientAddUpdate(props: IClientAddUpdateProps) {

  const theme = useTheme();
  const { addClient, updateClient, selectedClient, setSelectedClient, clientsError, clearClientErrors, clientsLoading } = useAmaWorkflowContext();
  const formProps = useForm<IFormInputs>({ defaultValues: getDefaultFormValues(selectedClient) });

  const onSubmit: SubmitHandler<IFormInputs> = async (data: IFormInputs) => {

    // Construct IClient for add/update
    const client : IClient = {
      ClientId: data.ClientId,
      DisplayName: data.DisplayName,
      IsActive: data.IsActive,
      IsStrictMode: data.IsStrictMode,
      IsAutoStart: data.IsAutoStart,
      IsCensusHistoricAvailable: data.IsCensusHistoricAvailable,
      CensusCurrentYearSnapshot: data.CensusCurrentYearSnapshot,
      NotificationRecipients: data.NotificationRecipients,
      AmaPipeline: {
        Description: data.Description,
        SourceFiles: data.ManifestItems,
        WorkflowArn: selectedClient ? selectedClient.AmaPipeline?.WorkflowArn : '',
        BronzeDb: selectedClient ? selectedClient.AmaPipeline?.BronzeDb : '',
        SilverDb: selectedClient ? selectedClient.AmaPipeline?.SilverDb : '',
        GoldDb: selectedClient ? selectedClient.AmaPipeline?.GoldDb : '',
        BronzeGlueJobName: selectedClient ? selectedClient.AmaPipeline?.BronzeGlueJobName : '',
        SilverGlueJobName: data.SilverGlueJobName,
        GoldGlueJobName: data.GoldGlueJobName }};

    // API call
    const apiCall = (props.mode === EditMode.Add) ? addClient : updateClient;
    const success = await apiCall(client);
    
    if (success) {

      // Clear selected client
      setSelectedClient(null);

      // Return to list view
      props.setEditMode(EditMode.View);   
    }
  };

  const handleCancelButtonClick = () => {
    setSelectedClient(null);
    props.setEditMode(EditMode.View);    
  };

  const handleModalOkButtonClick = () => {
    clearClientErrors();
  };

  // const logFormErrors = () => {
  //   console.log(`ClientAddUpdate.DEBUG :: form errors: ${formProps.formState.errors}`);
  // }

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Box id="inner" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minWidth: '600px' }}>
        <FormProvider {...formProps}>
          <form 
            noValidate 
            onSubmit={formProps.handleSubmit(onSubmit)}>
            
            {/* Loading overlay */}
            {clientsLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'rgba(255, 255, 255, 0.6)',
                  zIndex: 999,
                }}>
                <CircularProgress />           
              </div>
            )}

            <Stack spacing={2} sx={{ width: '600px', margin: 0, padding: 0, mb: 10 }}>

              {/* Section header / IsActive checkbox */}  
              <Grid 
                container 
                alignItems="center"
                sx={{ height: 35 }}>              
                <Grid item xs={6}>
                  <Typography component="h6" sx={{ color: theme.palette.text.secondary }}>
                    Client Details
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <FormControlLabel
                    label="Active"
                    control={
                      <Controller
                        name="IsActive"
                        control={formProps.control}
                        render={({ field }) => (
                          <Checkbox
                            color="primary"
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    }
                  />
                </Grid>
              </Grid>

              {/* ClientId */}          
              <TextField 
                label='Client ID'
                type='text'
                disabled={props.mode === EditMode.Edit}
                error={Boolean(formProps.formState.errors.ClientId)}
                helperText={formProps.formState.errors.ClientId?.message}
                {...formProps.register(
                  'ClientId', 
                  { 
                    required: 'ClientId is required',
                    pattern: 
                    {
                      value: /^[a-z_]+$/,
                      message: 'Client ID can only contain lowercase letters and underscores.' 
                    }
                  })
                }
              />

              {/* DisplayName */}
              <TextField 
                label='Client display Name'
                type='text'
                error={Boolean(formProps.formState.errors.DisplayName)}
                helperText={formProps.formState.errors.DisplayName?.message}
                {...formProps.register(
                  'DisplayName', 
                  { required: 'DisplayName is required' })
                }
              />

              {/* Description */}
              <TextField 
                label='Pipeline description (optional)'
                type='text'
                helperText={formProps.formState.errors.Description?.message}
                {...formProps.register('Description')}
              />

              {/* NotificationRecipients */}
              <TextField 
                label='Notification recipients (optional csv string)'
                type='text'
                helperText={formProps.formState.errors.NotificationRecipients?.message}
                error={Boolean(formProps.formState.errors.NotificationRecipients)}
                {...formProps.register(
                  'NotificationRecipients',
                  {
                    pattern: 
                    {
                      value: /^$|^(\s*[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}\s*)(,\s*[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}\s*)*$/,
                      message: 'Must be comma separated list of email addresses.' 
                    }
                  }
                )}
              />

              {/* Section header - Glue Job Configuration */}
              <Typography component="h6" sx={{ color: theme.palette.text.secondary, pt: 2 }}>
                Glue Job Configuration
              </Typography>

              {/* SilverGlueJobName */}
              <TextField 
                label='Silver Glue Job Name (ama_[client]_silver_script)'
                type='text'
                helperText={formProps.formState.errors.SilverGlueJobName?.message}
                error={Boolean(formProps.formState.errors.SilverGlueJobName)}
                {...formProps.register(
                  'SilverGlueJobName',
                  {
                    pattern: 
                    {
                      value: /^$|^ama_.*/,
                      message: 'Glue job name must start with prefix "ama_"' 
                    }
                  }
                )}
              />

              {/* GoldGlueJobName */}
              <TextField 
                label='Gold Glue Job Name (ama_default_gold_script)'
                type='text'
                helperText={formProps.formState.errors.GoldGlueJobName?.message}
                error={Boolean(formProps.formState.errors.GoldGlueJobName)}
                {...formProps.register(
                  'GoldGlueJobName',
                  {
                    pattern: 
                    {
                      value: /^$|^ama_.*/,
                      message: 'Glue job name must start with prefix "ama_"' 
                    }
                  }
                )}
              />              

              <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>

                {/* IsStrictMode */}
                <Tooltip title="When strict mode is enabled, Glue job processing will terminate upon error. Disabling strict mode during job configuration can be helpful for debugging but should always be enabled in production.">
                  <FormControlLabel
                    label="Strict Mode"
                    sx={{ pr: 4 }}
                    control={
                      <Controller
                        name="IsStrictMode"
                        control={formProps.control}
                        render={({ field }) => (
                          <Checkbox                          
                            color="primary"
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    }
                  />
                </Tooltip>

                {/* IsAutoStart */}
                <Tooltip title="When auto-start is enabled, Glue job processing is automatically triggered when all required files have been uploaded. When disabled the Glue job must be manually started.">
                  <FormControlLabel
                    label="Auto Start"
                    control={
                      <Controller
                        name="IsAutoStart"
                        control={formProps.control}
                        render={({ field }) => (
                          <Checkbox
                            color="primary"
                            checked={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    }
                  />
                </Tooltip>
              </Box>

              {/* Section header - Snapshot Manifest */}
              <Typography 
                component="h6" 
                pt={2} mb={2}
                sx={{ color: theme.palette.text.secondary }}>
                Snapshot Manifest
              </Typography>

              {/* Manifest builder */}
              <ManifestBuilder fieldsArrayKey='ManifestItems'/>

              {/* Save/Cancel buttons */}
              <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end" width={600}>
                <Button type='submit' variant='contained' color='primary'>Save</Button>
                <Button variant='outlined' color='primary' onClick={handleCancelButtonClick}>Cancel</Button>
                {/* <Button variant='outlined' color='primary' onClick={() => logFormErrors()}>Debug</Button> */}
              </Stack>   

            </Stack>
          </form>

          {/* Remove when dev complete */}
          {/* <DevTool control={formProps.control}/> */}

        </FormProvider>

        {/* Error modal */}
        <Modal
          open={Boolean(clientsError)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: 24,
            p: 4,}}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              Error
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4 }}>
              {clientsError}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" color="primary" onClick={handleModalOkButtonClick}>Ok</Button>
            </Box>
          </Box>
        </Modal>

      </Box>
    </Box>
  );
}

const getDefaultFormValues = (client: IClient | null): IFormInputs => {
  
  if (client) {    
    return {
      ClientId: client.ClientId,
      DisplayName: client.DisplayName || '',
      Description: client.AmaPipeline?.Description || '',
      SilverGlueJobName: client.AmaPipeline?.SilverGlueJobName || '',
      GoldGlueJobName: client.AmaPipeline?.GoldGlueJobName || '',
      NotificationRecipients: client.NotificationRecipients || '',
      IsActive: client.IsActive,
      IsStrictMode: client.IsStrictMode,
      IsAutoStart: client.IsAutoStart,
      IsCensusHistoricAvailable: client.IsCensusHistoricAvailable,
      CensusCurrentYearSnapshot: client.CensusCurrentYearSnapshot,
      ManifestItems: client.AmaPipeline?.SourceFiles || [] }
  }
  
  return {
    ClientId: '',
    DisplayName: '',
    Description: '',
    SilverGlueJobName: '',
    GoldGlueJobName: '',
    NotificationRecipients: '',
    IsActive: true,
    IsStrictMode: true,
    IsAutoStart: true,
    IsCensusHistoricAvailable: false,
    CensusCurrentYearSnapshot: '',
    ManifestItems: [{ Category: '', PiiAttributes: ''}]}
};